/**
 * 發送LOG到 Teams 頻道
 * @param {string} message 要發送的訊息文字
 */
export async function sendToTeams(message) {
  if (
    typeof window == "undefined" ||
    !window.env ||
    !window.env.REACT_APP_ENABLE_TEAMS_ERROR_REPORT
  ) {
    return;
  }
  try {
    const webhookUrl = window.env.REACT_APP_TEAMS_WEBHOOK_URL;

    if (typeof message !== "string") {
      message = JSON.stringify(message);
    }

    const res = await fetch(webhookUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: message,
    });

    if (!res.ok) {
      const txt = await res.text();
      console.error("[TeamsWebhook] 傳送失敗:", res.status, txt);
    } else {
      console.log("[TeamsWebhook] 傳送成功");
    }
  } catch (err) {
    console.error("[TeamsWebhook] 發送錯誤:", err);
  }
}

/**
 * 處理window on error事件
 * @param
 */
export async function handleWindowError(event) {
  try {
    const isResourceError =
      (event.target && event.target.src) || event.target.href;

    const err = event.error;
    let errorInfo;

    if (err instanceof Error) {
      // 如果是Error物件，無法直接JSON.stringify，要先展開
      errorInfo = {
        name: err.name,
        message: err.message,
        stack: err.stack,
      };
    } else if (err && typeof err === "object") {
      // 可能是 DOMException 或自訂錯誤
      try {
        errorInfo = JSON.parse(
          JSON.stringify(err, Object.getOwnPropertyNames(err))
        );
      } catch {
        errorInfo = { toString: String(err) };
      }
    } else {
      errorInfo = err ? err : null;
    }
    sendToTeams(
      getAdaptiveCard({
        type: isResourceError ? "resource-error" : "js-error",
        message:
          event.message ||
          ((event.target && event.target.src) || event.target.href) +
            "檔案載入失敗",
        detail: errorInfo,
        time: new Date().toLocaleString(),
      })
    );
  } catch (err) {
    console.error(err);
  }
}

/**
 * 處理promise rejection error的事件
 * @param {*} event
 */
export async function handlePromiseRejection(event) {
  try {
    const reason = event.reason;

    let message;

    if (typeof reason === "string") {
      message = reason;
    } else if (reason instanceof Error) {
      message = `${reason.message}\n${reason.stack || ""}`;
    } else if (typeof reason === "object" && reason !== null) {
      // 可能是自訂錯誤或 Axios 等
      message = JSON.stringify(reason, Object.getOwnPropertyNames(reason));
    } else {
      message = String(reason);
    }

    sendToTeams(
      getAdaptiveCard({
        type:
          reason.response && reason.response.status ? "api-error" : "js-error",
        message: message,
        detail: reason,
        time: new Date().toLocaleString(),
      })
    );
  } catch (err) {
    console.error(err);
  }
}

/*
 * 註冊 Axios API的全域錯誤攔截器
 */
export function registerApiErrorReport(axiosInstance) {
  axiosInstance.interceptors.response.use(
    (response) => {
      if (response.data.errCode !== 200) {
        const config = response.config || {};
        const url = config.url || "(unknown)";
        const method = (config.method || "GET").toUpperCase();
        const requestBody = config.data || null;
        try {
          sendToTeams(
            getAdaptiveCard({
              type: "api-error",
              url,
              message: response.data.errMessage || "",
              detail: response.data,
              time: new Date().toLocaleString(),
              extra: {
                method,
                requestBody,
                params: config.params || null,
              },
            })
          );
        } catch (err) {
          console.warn("sendToTeams error:", err);
        }
      }

      return response;
    },
    (error) => {
      const config = error.config || {};
      const url = config.url || "(unknown)";
      const method = (config.method || "GET").toUpperCase();

      //  拿到 request body
      let requestBody = null;
      try {
        if (typeof config.data === "string") {
          // axios 會自動把 JSON 轉成字串送出，所以要還原
          requestBody = JSON.parse(config.data);
        } else {
          requestBody = config.data;
        }
      } catch (e) {
        requestBody = config.data;
      }

      const shouldReport =
        !error.response || (error.response && error.response.status >= 400);

      if (shouldReport) {
        try {
          sendToTeams(
            getAdaptiveCard({
              type: "api-error",
              message:
                error.message ||
                (error.response && error.response.statusText) ||
                "Unknown API error",
              detail:
                error.response && error.response.data
                  ? error.response.data
                  : null,
              time: new Date().toLocaleString(),
              extra: { url, method, requestBody },
            })
          );
        } catch (err) {
          console.warn("sendToTeams error:", err);
        }
      }

      return Promise.reject(error);
    }
  );
}

/**
 * 長出teams的adaptive card樣式設定資料
 */
export function getAdaptiveCard({ type, message, detail, url, time, extra }) {
  const cardBase = {
    type: "message",
    attachments: [
      {
        contentType: "application/vnd.microsoft.card.adaptive",
        content: {
          $schema: "http://adaptivecards.io/schemas/adaptive-card.json",
          type: "AdaptiveCard",
          version: "1.5",
          body: [],
        },
      },
    ],
  };

  // 組合卡片內容
  cardBase.attachments[0].content.body = [
    {
      type: "Container",
      spacing: "large",
      style: "emphasis",
      items: [
        {
          type: "TextBlock",
          weight: "bolder",
          text: "鼎泰豐線上內用點餐REACT站台_log",
          size: "Large",
          height: "stretch",
          wrap: true,
        },
      ],
      bleed: true,
    },
    // push factSet
  ];

  // 訊息類型
  let config = {
    title: "未知錯誤",
    color: "default",
    emoji: "⚠️",
  };

  switch (type) {
    case "js-error":
      config = { title: "前端 JS 錯誤", color: "Attention", emoji: "💥" };
      break;
    case "api-error":
      config = { title: "API 錯誤", color: "Warning", emoji: "🌐" };
      break;
    case "resource-error":
      config = { title: "資源載入失敗", color: "Good", emoji: "📦" };
      break;
    default:
      config = { title: "其他錯誤", color: "Default", emoji: "⚠️" };
      break;
  }

  // adaptive card的一種區塊名稱:factSet
  const factSet = {
    type: "FactSet",
    spacing: "large",
    facts: [
      {
        title: "訊息類型",
        value: `${config.emoji} ${config.title}`,
      },
      {
        title: "發生時間",
        value: `${time || new Date().toLocaleString("zh-TW")}`,
      },
    ],
  };

  // 如果訊息內容過長 需要使用toggle功能
  const toggle_factSet = {
    type: "FactSet",
    spacing: "Small",
    id: "message_block",
    facts: [],
  };

  if (!!message) {
    if (message.length > 200) {
      toggle_factSet.facts.push({
        title: "訊息內容",
        value: message.slice(0, 200) + " ......",
      });
    } else {
      factSet.facts.push({
        title: "訊息內容",
        value: message,
      });
    }
  }

  if (!!url) {
    factSet.facts.push({ title: "url", value: url });
  }

  if (!!detail) {
    if (JSON.stringify(detail, null, 2).length > 2) {
      factSet.facts.push({
        title: type === "api-error" ? `Response` : `詳細資訊`,
        value: JSON.stringify(detail, null, 2),
      });
    }
  }

  if (!!extra) {
    Object.keys(extra).forEach((key) => {
      if (!extra[key]) {
        return;
      }
      if (typeof extra[key] === "object") {
        extra[key] = JSON.stringify(extra[key], null, 2);
      }
      factSet.facts.push({
        title: key,
        value: extra[key],
      });
    });
  }

  cardBase.attachments[0].content.body.push(factSet);

  if (toggle_factSet.facts.length > 0) {
    cardBase.attachments[0].content.body.push(toggle_factSet);
    cardBase.attachments[0].content.body.push({
      type: "ActionSet",
      id: "show_more_button",
      actions: [
        {
          type: "Action.ToggleVisibility",
          title: "顯示更多",
          iconUrl: "icon:ArrowDownload",
          targetElements: [
            "show_more_button",
            "show_less_button",
            "message_block",
            "message_detail",
          ],
        },
      ],
    });
  }

  // 按下 顯示更多 按鈕 顯示message詳細內容
  if (!!message && message.length > 200) {
    cardBase.attachments[0].content.body.push({
      type: "FactSet",
      spacing: "Small",
      id: "message_detail",
      isVisible: false,
      facts: [
        {
          title: "訊息內容",
          value: message,
        },
      ],
    });
    cardBase.attachments[0].content.body.push({
      type: "ActionSet",
      id: "show_less_button",
      isVisible: false,
      actions: [
        {
          type: "Action.ToggleVisibility",
          title: "顯示更多",
          iconUrl: "icon:ArrowExportUp",
          targetElements: [
            "show_more_button",
            "show_less_button",
            "message_block",
            "message_detail",
          ],
        },
      ],
    });
  }

  return cardBase;
}
